import { ConfirmationDialogProvider } from '@tempo-io/roadmapping-frontend-confirm-dialog';
import type { ReactNode, RefObject } from 'react';
import { ContextMenuProvider } from '@/common/components/ui/swimlane/context-menu';
import { defaultFormatDate } from '@/common/components/ui/swimlane/utils';
import { type SwimlaneApi, SwimlanePrivateApiContext, useSwimlaneApiInitialization } from '../api';
import type { SwimlaneRootSlotProps } from '../types/slots';
import type { SwimlaneRootProps } from '../types/swimlane';
import { ColRowHighlightingProvider } from './ColRowHighlightContext';
import { SwimlaneRootPropsContext } from './SwimlaneRootPropsContext';
import { SwimlaneSlotsContext } from './SwimlaneSlotsContext';

export interface ISwimlaneProps extends SwimlaneRootProps, SwimlaneRootSlotProps {}

export const SwimlaneContextProvider = ({
  children,
  readOnly = true,
  isCompact = false,
  maxCardTitleLines = 3,
  isItemDateVisible = true,
  isItemDescriptionVisible = true,
  isItemParentVisible = true,
  isItemDetailsVisible = true,
  itemLabelSize = 'medium',
  maxSelectedCards = Number.MAX_SAFE_INTEGER,
  maxSubItemsPerItem = Number.MAX_SAFE_INTEGER,
  maxItemsPerCell = Number.MAX_SAFE_INTEGER,
  rowHeaderOrientation = 'vertical',
  rowHeaders,
  isPublicationMode = false,
  cells = new Map(),
  items = new Map(),
  isHeadersSticky = true,
  backgroundColor = 'transparent',
  areLabelsEnabledInCompactSL = false,
  slots,
  slotProps = {},
  formatDate = defaultFormatDate,
  shouldHideInlineAdd = false,
  isSubItemFeaturesEnabled = true,
  isSubItemsVisible = true,
  childItemLevels = 1,
  showExpandedSubItems = false,
  ...restProps
}: ISwimlaneProps & { children: ReactNode }) => {
  const overriddenRowHeaderOrientation =
    rowHeaders.length === 0 && !restProps.rowAxisLabelText ? 'vertical' : rowHeaderOrientation;
  const isHeaderMenuDisabled =
    restProps.isHeaderMenuDisabled !== undefined ? restProps.isHeaderMenuDisabled : readOnly;

  // Need to split and memoize config/props/etc to avoid unnecessary re-renders
  const props = {
    ...restProps,
    readOnly,
    isCompact,
    maxCardTitleLines,
    isItemDateVisible,
    isItemDescriptionVisible,
    isItemParentVisible,
    isItemDetailsVisible,
    itemLabelSize,
    maxSelectedCards,
    maxSubItemsPerItem,
    maxItemsPerCell,
    isPublicationMode,
    cells,
    items,
    isHeadersSticky,
    backgroundColor,
    areLabelsEnabledInCompactSL,
    isHeaderMenuDisabled,
    rowHeaderOrientation: overriddenRowHeaderOrientation,
    rowHeaders,
    formatDate,
    shouldHideInlineAdd,
    isSubItemFeaturesEnabled,
    isSubItemsVisible,
    childItemLevels,
    showExpandedSubItems: isSubItemFeaturesEnabled && showExpandedSubItems,
  };
  const privateApiRef = useSwimlaneApiInitialization(props.apiRef as RefObject<SwimlaneApi>);

  return (
    <SwimlaneRootPropsContext.Provider value={props}>
      <SwimlaneSlotsContext.Provider value={{ slots, slotProps }}>
        <ColRowHighlightingProvider>
          <ConfirmationDialogProvider>
            <ContextMenuProvider>
              <SwimlanePrivateApiContext.Provider value={privateApiRef}>
                {children}
              </SwimlanePrivateApiContext.Provider>
            </ContextMenuProvider>
          </ConfirmationDialogProvider>
        </ColRowHighlightingProvider>
      </SwimlaneSlotsContext.Provider>
    </SwimlaneRootPropsContext.Provider>
  );
};
